// Canonical response-shape boundary for the shared API client.
export type ResponseKeyPath = readonly string[];

export interface ResponseKeyContract {
  /** Keep the complete wire shape. Use only for an API module with wire-native types. */
  readonly mode?: 'structured' | 'wire';
  /** Object paths whose property names are identifiers/data rather than schema fields. */
  readonly dynamicKeyPaths?: readonly ResponseKeyPath[];
  /** Paths containing open-ended JSON that must round-trip byte-for-byte by key. */
  readonly opaquePaths?: readonly ResponseKeyPath[];
}

declare module 'axios' {
  // biome-ignore lint/suspicious/noExplicitAny: Axios declaration merging must exactly match AxiosRequestConfig's upstream generic default.
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface AxiosRequestConfig<D = any> {
    responseKeyContract?: ResponseKeyContract;
    _debugStartTime?: number;
  }
}

/**
 * Record-valued response fields audited in `src/api` whose contents are data,
 * identifiers, provider JSON, schemas, statistics, or other open-ended state.
 * The containing field is still normalized; its subtree is not rewritten.
 */
export const OPAQUE_RESPONSE_RECORD_FIELDS = Object.freeze([
  'actionConfig',
  'actionPayload',
  'actionResult',
  // Agent role flags keyed by role id (endpoint_runtime, network_monitoring, ...).
  'agentRoles',
  'approvalGraphState',
  'args',
  'auditSections',
  'authConfig',
  'autoAttendants',
  'autoAssignRule',
  'automationConfig',
  'automationResult',
  'autofillSources',
  'autopopulateSources',
  'beforeSnapshot',
  'byAction',
  'byArea',
  'byCategory',
  'byClass',
  'byKind',
  'byIntegration',
  'byModule',
  'byTrayGroup',
  'byPriority',
  'bySeverity',
  'bySource',
  'byStatus',
  'byTrust',
  'byType',
  'callMetadata',
  'callQueues',
  'canvasData',
  'categoryBreakdown',
  'config',
  'configSchema',
  'configValues',
  'configuration',
  'content',
  'context',
  'contextData',
  'credentials',
  'customFields',
  'defaultConfig',
  'details',
  'doneByCategory',
  'dryRunOutput',
  'entitiesStatus',
  'errorDetails',
  'evidenceBlob',
  'executionOutput',
  'fieldCompleteness',
  'filledSections',
  'headers',
  'inputMappings',
  'inputSchema',
  'inputValues',
  'itemData',
  'metadata',
  'methodBreakdown',
  'migrationResult',
  'options',
  'output',
  'outputSchema',
  'outputValues',
  'parameters',
  'params',
  'payload',
  'permissions',
  'presentationData',
  'properties',
  'resultData',
  'resultDetails',
  'settings',
  'sources',
  'storageConfig',
  'switches',
  'termDiscounts',
  'thresholds',
  'triggerConfig',
  'triggerEvent',
  'tutorials',
  'tweaks',
  'tweaksApplied',
  'variables',
  'vendorResults',
  'vmBootDetails',
  'wiredInputs',
  'wmiData',
  'snmpData',
] as const);

export const STRUCTURED_RESPONSE_KEYS: ResponseKeyContract = Object.freeze({
  mode: 'structured' as const,
  opaquePaths: Object.freeze(
    OPAQUE_RESPONSE_RECORD_FIELDS.map((field) => Object.freeze(['**', field]))
  ),
});

export const WIRE_RESPONSE_KEYS: ResponseKeyContract = Object.freeze({
  mode: 'wire' as const,
});

/**
 * Build a response contract at the owning API module.
 *
 * Paths use the frontend (camelCase) structured-field names. `*` matches one
 * array item or dynamic-map value; `**` matches zero or more path segments.
 */
export function responseKeyContract({
  dynamicKeyPaths = [],
  opaquePaths = [],
}: Omit<ResponseKeyContract, 'mode'>): ResponseKeyContract {
  return Object.freeze({
    mode: 'structured' as const,
    dynamicKeyPaths: Object.freeze(dynamicKeyPaths.map((path) => Object.freeze([...path]))),
    opaquePaths: Object.freeze(opaquePaths.map((path) => Object.freeze([...path]))),
  });
}

function snakeToCamel(value: string): string {
  return value.replace(/_([a-z0-9])/g, (_, character: string) => character.toUpperCase());
}

function pathMatches(path: readonly string[], pattern: ResponseKeyPath): boolean {
  function visit(pathIndex: number, patternIndex: number): boolean {
    if (patternIndex === pattern.length) return pathIndex === path.length;

    const part = pattern[patternIndex];
    if (part === '**') {
      return (
        visit(pathIndex, patternIndex + 1) ||
        (pathIndex < path.length && visit(pathIndex + 1, patternIndex))
      );
    }

    if (pathIndex >= path.length || (part !== '*' && part !== path[pathIndex])) {
      return false;
    }
    return visit(pathIndex + 1, patternIndex + 1);
  }

  return visit(0, 0);
}

function matchesAny(path: readonly string[], patterns: readonly ResponseKeyPath[]): boolean {
  return patterns.some((pattern) => pathMatches(path, pattern));
}

/** Apply a schema-aware response contract without mutating the wire payload. */
export function applyResponseKeyContract(
  data: unknown,
  contract: ResponseKeyContract = STRUCTURED_RESPONSE_KEYS
): unknown {
  if (contract.mode === 'wire') return data;

  const dynamicKeyPaths = contract.dynamicKeyPaths ?? [];
  const opaquePaths = contract.opaquePaths ?? [];

  function transform(value: unknown, path: readonly string[]): unknown {
    if (value === null || value === undefined || matchesAny(path, opaquePaths)) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map((item) => transform(item, [...path, '*']));
    }
    if (typeof value !== 'object') return value;

    const dynamicKeys = matchesAny(path, dynamicKeyPaths);
    const result: Record<string, unknown> = {};
    for (const [wireKey, child] of Object.entries(value)) {
      const frontendKey = dynamicKeys ? wireKey : snakeToCamel(wireKey);
      const childPath = [...path, dynamicKeys ? '*' : frontendKey];
      result[frontendKey] = transform(child, childPath);
    }
    return result;
  }

  return transform(data, []);
}
